import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert
} from '@mui/material';
import {
  RestorePage as RestoreIcon,
  PlayArrow as ContinueIcon,
  Delete as DeleteIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

interface WorkloadDraftStudent {
  studentName?: string;
  studentCode?: string;
  major?: string;
  scores: Record<string, number | string>;
  feedback?: string;
  updatedAt?: string;
}

interface WorkloadDraftPayload {
  userId?: string;
  courseId?: string;
  courseCode?: string;
  courseName?: string;
  academicYear?: string;
  semester?: string;
  major?: string;
  updatedAt?: string;
  students: Record<string, WorkloadDraftStudent>;
}

export const WorkloadDraftAlertModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<WorkloadDraftPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isEligibleRole = ['SUPERVISOR', 'ADMIN', 'STAFF'].includes(user?.role);

  useEffect(() => {
    // Only check if eligible role and not currently on the workload evaluation page
    if (!isEligibleRole || !user?.id) return;
    if (location.pathname === '/dashboard/workload-evaluation') return;

    // Check if modal was already dismissed during this active browser session
    const isDismissed = sessionStorage.getItem(`tpms_dismissed_draft_${user.id}`);
    if (isDismissed) return;

    const checkDraft = async () => {
      try {
        const localDraftKey = `tpms_workload_draft_${user.id}`;
        let foundDraft: WorkloadDraftPayload | null = null;

        // 1. Check LocalStorage first for instant detection
        const localDraftStr = localStorage.getItem(localDraftKey);
        if (localDraftStr) {
          try {
            const parsed = JSON.parse(localDraftStr);
            // Support both new structured format and legacy flat placement map
            if (parsed.students && Object.keys(parsed.students).length > 0) {
              foundDraft = parsed;
            } else if (typeof parsed === 'object' && Object.keys(parsed).length > 0) {
              // Legacy format: { [placementId]: { [itemId]: score } }
              const convertedStudents: Record<string, WorkloadDraftStudent> = {};
              Object.keys(parsed).forEach(pId => {
                if (parsed[pId] && typeof parsed[pId] === 'object' && Object.keys(parsed[pId]).length > 0) {
                  convertedStudents[pId] = {
                    studentName: 'นักศึกษาในแบบร่าง',
                    studentCode: pId.slice(0, 8),
                    scores: parsed[pId]
                  };
                }
              });
              if (Object.keys(convertedStudents).length > 0) {
                foundDraft = {
                  students: convertedStudents,
                  updatedAt: new Date().toISOString()
                };
              }
            }
          } catch (e) {
            console.warn('Failed to parse local draft:', e);
          }
        }

        // 2. If not found locally, check Cloud/Server backup
        if (!foundDraft) {
          try {
            const res = await api.get('/workload-evaluations/draft');
            if (res.data?.draft && res.data.draft.students && Object.keys(res.data.draft.students).length > 0) {
              foundDraft = res.data.draft;
              // Mirror back to local storage
              localStorage.setItem(localDraftKey, JSON.stringify(foundDraft));
            }
          } catch (e) {
            // Server draft check non-fatal
          }
        }

        if (foundDraft && foundDraft.students && Object.keys(foundDraft.students).length > 0) {
          setDraft(foundDraft);
          setOpen(true);
        }
      } catch (err) {
        console.error('Error checking workload draft:', err);
      }
    };

    checkDraft();
  }, [location.pathname, user?.id, isEligibleRole]);

  const handleContinue = () => {
    setOpen(false);
    sessionStorage.setItem(`tpms_dismissed_draft_${user.id}`, 'true');
    navigate('/dashboard/workload-evaluation', {
      state: {
        restoreDraft: true,
        courseId: draft?.courseId,
        academicYear: draft?.academicYear,
        semester: draft?.semester,
        major: draft?.major
      }
    });
  };

  const handleDismiss = () => {
    setOpen(false);
    sessionStorage.setItem(`tpms_dismissed_draft_${user.id}`, 'true');
  };

  const handleDiscard = async () => {
    if (!window.confirm('ยืนยันที่จะละทิ้งคะแนนแบบร่างที่ยังไม่ได้บันทึกทั้งหมด? ข้อมูลคะแนนที่กรอกค้างไว้จะถูกลบ')) {
      return;
    }

    try {
      setLoading(true);
      const localDraftKey = `tpms_workload_draft_${user.id}`;
      localStorage.removeItem(localDraftKey);
      try {
        await api.delete('/workload-evaluations/draft');
      } catch (e) {
        // Non-fatal
      }
      setDraft(null);
      setOpen(false);
      sessionStorage.setItem(`tpms_dismissed_draft_${user.id}`, 'true');
    } catch (err) {
      console.error('Failed to clear draft:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!open || !draft || !draft.students) return null;

  const studentEntries = Object.entries(draft.students);
  const totalStudents = studentEntries.length;

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3.5,
            p: 1,
            boxShadow: '0 20px 45px rgba(0,0,0,0.18)'
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: 'warning.light',
              color: 'warning.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WarningIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
              แจ้งเตือน: พบรายการบันทึกคะแนนภาระงานค้างอยู่
            </Typography>
            <Typography variant="caption" color="textSecondary">
              มีข้อมูลคะแนนที่อาจารย์กรอกค้างไว้และยังไม่ได้กดบันทึก
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`ค้างบันทึก ${totalStudents} คน`}
          color="warning"
          size="small"
          sx={{ fontWeight: 700 }}
        />
      </DialogTitle>

      <DialogContent sx={{ py: 1.5 }}>
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
          ระบบตรวจพบคะแนนภาระงานที่กรอกไว้แต่<b>ยังไม่ได้กดบันทึก</b> (อาจเกิดจากไฟดับ การเชื่อมต่อขัดข้อง หรือออกจากระบบก่อนบันทึก) ท่านสามารถกดเข้าสู่การกรอกคะแนนต่อได้ทันทีเพื่อตรวจสอบและบันทึกข้อมูล
        </Alert>

        {/* Course & Term Meta info */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, mb: 2.5, bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                รายวิชาฝึกประสบการณ์
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {draft.courseCode ? `${draft.courseCode} ` : ''}{draft.courseName || 'ไม่ระบุรายวิชา'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                ภาคเรียน / ปีการศึกษา
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {draft.semester && draft.semester !== 'ALL' ? `ภาคเรียนที่ ${draft.semester}` : 'ทุกภาคเรียน'} / {draft.academicYear && draft.academicYear !== 'ALL' ? draft.academicYear : 'ทุกปี'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                บันทึกแบบร่างล่าสุดเมื่อ
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {draft.updatedAt ? new Date(draft.updatedAt).toLocaleString('th-TH') : '-'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Student List Table */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="action" fontSize="small" /> รายชื่อนักศึกษาที่มีคะแนนค้างการบันทึก:
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 240 }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: 60 }}>ลำดับ</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>รหัสนักศึกษา</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ชื่อ - สกุล</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>สาขาวิชา</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>จำนวนข้อที่กรอกคะแนน</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studentEntries.map(([placementId, item], index) => {
                const filledItemCount = Object.values(item.scores || {}).filter(v => v !== '' && v !== null && v !== undefined).length;
                return (
                  <TableRow key={placementId} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{item.studentCode || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{item.studentName || 'นักศึกษา'}</TableCell>
                    <TableCell>{item.major || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${filledItemCount} ข้อ`}
                        size="small"
                        color={filledItemCount > 0 ? "warning" : "default"}
                        variant="outlined"
                        sx={{ height: 22, fontWeight: 700, fontSize: '0.72rem' }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', bgcolor: '#f8fafc', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
        <Button
          color="error"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={handleDiscard}
          disabled={loading}
          sx={{ fontWeight: 600 }}
        >
          ล้างแบบร่างนี้
        </Button>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleDismiss}
            disabled={loading}
            sx={{ borderRadius: 2, px: 2, fontWeight: 600 }}
          >
            ไว้ภายหลัง
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ContinueIcon />}
            onClick={handleContinue}
            disabled={loading}
            sx={{ borderRadius: 2, px: 2.5, fontWeight: 700, boxShadow: '0 4px 12px rgba(25,118,210,0.25)' }}
          >
            เข้าสู่การกรอกคะแนนต่อทันที
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WorkloadDraftAlertModal;